
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DialogFooter } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { createCity } from "@/services/cityService";
import { supabase } from "@/integrations/supabase/client";
import { City, Organization } from "@/types/flight";

const citySchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  code: z.string().min(2, "Code must be at least 2 characters").max(5, "Code must be at most 5 characters"),
  organization_id: z.string().optional().nullable(),
});

type FormValues = z.infer<typeof citySchema>;

interface CityFormProps {
  city?: City | null;
  organizations: Organization[];
  onSubmit: () => void;
}

export function CityForm({ city, organizations, onSubmit }: CityFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(citySchema),
    defaultValues: {
      name: city?.name || "",
      code: city?.code || "",
      organization_id: city?.organization_id || null,
    },
  });

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    
    try {
      if (city) {
        // Update existing city
        const { error } = await supabase
          .from('cities')
          .update({
            name: values.name,
            code: values.code,
            organization_id: values.organization_id === "global" ? null : values.organization_id,
          })
          .eq('id', city.id);

        if (error) throw error;

        toast({
          title: "City updated",
          description: `${values.name} has been updated successfully.`,
        });
      } else {
        // Create new city
        await createCity({
          name: values.name,
          code: values.code,
          organization_id: values.organization_id === "global" ? undefined : values.organization_id || undefined,
        });

        toast({
          title: "City created",
          description: `${values.name} has been created successfully.`,
        });
      }
      
      onSubmit();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save city",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input placeholder="City name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Code</FormLabel>
              <FormControl>
                <Input placeholder="City code (e.g., WDH)" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="organization_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Organization (Optional)</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                value={field.value || "global"}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select an organization" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="global">Global (No Organization)</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : city ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
